import { isUserLoggedIn } from '../utils/is-logged-in.js';
import { inferApiBase } from '../utils/api-base.js';

/**
 * HTTP access to the Intelligence API.
 *
 * Every endpoint is organization-scoped on the server from the session cookie;
 * the client never sends an organization id. A disabled organization gets 404
 * from every path, which callers surface as "not available" rather than an error.
 */
export class IntelligenceGateway {
  constructor({ fetch, apiBase } = {}) {
    if (typeof fetch === 'function') {
      this.fetch = fetch;
    } else if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
      this.fetch = window.fetch.bind(window);
    } else {
      this.fetch = null;
    }
    if (!this.fetch) {
      throw new Error('fetch implementation is required');
    }
    this.apiBase = apiBase || inferApiBase();
  }

  #url(path, query) {
    let url = this.apiBase + '/api/intelligence' + path;
    if (query) {
      const params = new URLSearchParams();
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
      const qs = params.toString();
      if (qs) url += '?' + qs;
    }
    return url;
  }

  #json(method, path, body) {
    return this.fetch(this.#url(path), {
      method,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  // ── Scopes ─────────────────────────────────────────────────────────

  async listScopes() {
    // Not an answer, and marked as such: the mode can be revealed before the
    // session is restored, and a list fetched then must not be cached as
    // "this organization has no scopes".
    if (!isUserLoggedIn()) return { scopes: [], skipped: true };
    const res = await this.fetch(this.#url('/scopes'), { credentials: 'include' });
    return this.#parse(res);
  }

  async createScope({ parentId, scopeType, name, description }) {
    const res = await this.#json('POST', '/scopes', { parentId, scopeType, name, description });
    return this.#parse(res);
  }

  async updateScope(scopeId, updates) {
    if (!scopeId) throw new Error('scopeId is required');
    const res = await this.#json('PUT', '/scopes/' + encodeURIComponent(scopeId), updates);
    return this.#parse(res);
  }

  async addScopeParent(scopeId, parentScopeId) {
    if (!scopeId || !parentScopeId) throw new Error('scopeId and parentScopeId are required');
    const res = await this.#json('POST', '/scopes/' + encodeURIComponent(scopeId) + '/parents', { parentScopeId });
    return this.#parse(res);
  }

  async removeScopeParent(scopeId, parentScopeId) {
    if (!scopeId || !parentScopeId) throw new Error('scopeId and parentScopeId are required');
    const res = await this.fetch(
      this.#url('/scopes/' + encodeURIComponent(scopeId) + '/parents/' + encodeURIComponent(parentScopeId)),
      { method: 'DELETE', credentials: 'include' }
    );
    return this.#parse(res, true);
  }

  // Who sees which part of the organization (spec 015).
  /** Everyone in the organization, for choosing who to give a role to. */
  async listOrganizationPeople() {
    const res = await this.fetch(this.#url('/people'), { credentials: 'include' });
    return this.#parse(res);
  }

  /** Take somebody out of the organization: everything revoked, account suspended. */
  async removeMember(userId) {
    if (!userId) throw new Error('userId is required');
    const res = await this.fetch(this.#url('/people/' + encodeURIComponent(userId)), {
      method: 'DELETE',
      credentials: 'include',
    });
    return this.#parse(res);
  }

  // ── Invitations (Settings plan S5) ──────────────────────────────────

  async listInvitations() {
    const res = await this.fetch(this.#url('/invitations'), { credentials: 'include' });
    return this.#parse(res);
  }

  async invite({ email, scopeId, role }) {
    const res = await this.#json('POST', '/invitations', { email, scopeId, role });
    return this.#parse(res);
  }

  async revokeInvitation(invitationId) {
    if (!invitationId) throw new Error('invitationId is required');
    const res = await this.fetch(this.#url('/invitations/' + encodeURIComponent(invitationId)), {
      method: 'DELETE',
      credentials: 'include',
    });
    return this.#parse(res);
  }

  async resendInvitation(invitationId) {
    if (!invitationId) throw new Error('invitationId is required');
    const res = await this.#json('POST', '/invitations/' + encodeURIComponent(invitationId) + '/resend');
    return this.#parse(res);
  }

  /** What the signed-in member may do, and whether it is enforced. */
  async myAccess() {
    const res = await this.fetch(this.#url('/me'), { credentials: 'include' });
    return this.#parse(res);
  }

  async grantCapability(scopeId, { userId, capability, reason }) {
    const res = await this.fetch(
      this.#url('/scopes/' + encodeURIComponent(scopeId) + '/capabilities'),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, capability, reason }),
      }
    );
    return this.#parse(res);
  }

  async revokeCapability(scopeId, userId, capability) {
    const res = await this.fetch(
      this.#url(`/scopes/${encodeURIComponent(scopeId)}/capabilities/${encodeURIComponent(userId)}/${encodeURIComponent(capability)}`),
      { method: 'DELETE', credentials: 'include' }
    );
    return this.#parse(res);
  }

  /** Who holds a role or a granted capability at this scope (spec 026). */
  async listScopeMembers(scopeId) {
    const res = await this.fetch(
      this.#url('/scopes/' + encodeURIComponent(scopeId) + '/members'),
      { credentials: 'include' }
    );
    return this.#parse(res);
  }

  async assignRole(scopeId, { userId, role }) {
    const res = await this.fetch(
      this.#url('/scopes/' + encodeURIComponent(scopeId) + '/roles'),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, role }),
      }
    );
    return this.#parse(res);
  }

  async revokeRole(scopeId, userId, role) {
    const res = await this.fetch(
      this.#url(`/scopes/${encodeURIComponent(scopeId)}/roles/${encodeURIComponent(userId)}/${encodeURIComponent(role)}`),
      { method: 'DELETE', credentials: 'include' }
    );
    return this.#parse(res);
  }




  async setScopeVisibility(enforced) {
    const res = await this.fetch(this.#url('/scope-visibility'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enforced: enforced === true }),
    });
    return this.#parse(res);
  }

  async linkScopeTeam(scopeId, teamId) {
    if (!scopeId || !teamId) throw new Error('scopeId and teamId are required');
    const res = await this.#json('POST', '/scopes/' + encodeURIComponent(scopeId) + '/team', { teamId });
    return this.#parse(res);
  }

  async unlinkScopeTeam(scopeId) {
    if (!scopeId) throw new Error('scopeId is required');
    const res = await this.fetch(this.#url('/scopes/' + encodeURIComponent(scopeId) + '/team'), {
      method: 'DELETE',
      credentials: 'include',
    });
    return this.#parse(res);
  }

  // ── Entities, aliases, relationships ───────────────────────────────

  async listEntities({ scopeId, type, q } = {}) {
    if (!isUserLoggedIn()) return { entities: [] };
    const res = await this.fetch(this.#url('/entities', { scopeId, type, q }), { credentials: 'include' });
    return this.#parse(res);
  }

  async createEntity({ entityType, name, homeScopeId, description }) {
    const res = await this.#json('POST', '/entities', { entityType, name, homeScopeId, description });
    return this.#parse(res);
  }

  async updateEntity(entityId, updates) {
    if (!entityId) throw new Error('entityId is required');
    const res = await this.#json('PUT', '/entities/' + encodeURIComponent(entityId), updates);
    return this.#parse(res);
  }

  async addAlias(entityId, alias) {
    if (!entityId || !alias) throw new Error('entityId and alias are required');
    const res = await this.#json('POST', '/entities/' + encodeURIComponent(entityId) + '/aliases', { alias });
    return this.#parse(res);
  }

  async removeAlias(entityId, aliasId) {
    if (!entityId || !aliasId) throw new Error('entityId and aliasId are required');
    const res = await this.fetch(
      this.#url('/entities/' + encodeURIComponent(entityId) + '/aliases/' + encodeURIComponent(aliasId)),
      { method: 'DELETE', credentials: 'include' }
    );
    return this.#parse(res, true);
  }

  /** Alias lookup for the entity picker: typing "IAM" finds Identity Platform. */
  async resolveEntities(q) {
    if (!q) return { matches: [] };
    const res = await this.fetch(this.#url('/entities/resolve', { q }), { credentials: 'include' });
    return this.#parse(res);
  }

  async listRelationships({ scopeId } = {}) {
    if (!isUserLoggedIn()) return { relationships: [] };
    const res = await this.fetch(this.#url('/relationships', { scopeId }), { credentials: 'include' });
    return this.#parse(res);
  }

  async createRelationship({ fromKind, fromId, toKind, toId, relType, evidenceId }) {
    const res = await this.#json('POST', '/relationships', {
      fromKind, fromId, toKind, toId, relType, evidenceId,
    });
    return this.#parse(res);
  }

  async deleteRelationship(relationshipId) {
    if (!relationshipId) throw new Error('relationshipId is required');
    const res = await this.fetch(this.#url('/relationships/' + encodeURIComponent(relationshipId)), {
      method: 'DELETE',
      credentials: 'include',
    });
    return this.#parse(res, true);
  }

  // ── Evidence ───────────────────────────────────────────────────────

  async listEvidence(filters = {}) {
    if (!isUserLoggedIn()) return { evidence: [], total: 0, nextCursor: null };
    const res = await this.fetch(this.#url('/evidence', filters), { credentials: 'include' });
    return this.#parse(res);
  }

  async getEvidence(evidenceId) {
    if (!evidenceId) throw new Error('evidenceId is required');
    const res = await this.fetch(this.#url('/evidence/' + encodeURIComponent(evidenceId)), {
      credentials: 'include',
    });
    return this.#parse(res);
  }

  async createEvidence(evidence) {
    const res = await this.#json('POST', '/evidence', evidence);
    return this.#parse(res);
  }

  async updateEvidence(evidenceId, updates) {
    if (!evidenceId) throw new Error('evidenceId is required');
    const res = await this.#json('PUT', '/evidence/' + encodeURIComponent(evidenceId), updates);
    return this.#parse(res);
  }

  async retractEvidence(evidenceId, reason) {
    if (!evidenceId) throw new Error('evidenceId is required');
    const res = await this.#json('POST', '/evidence/' + encodeURIComponent(evidenceId) + '/retract', { reason });
    return this.#parse(res);
  }

  async linkEvidence(evidenceId, { toEvidenceId, linkType, note }) {
    if (!evidenceId) throw new Error('evidenceId is required');
    const res = await this.#json('POST', '/evidence/' + encodeURIComponent(evidenceId) + '/links', {
      toEvidenceId, linkType, note,
    });
    return this.#parse(res);
  }

  async unlinkEvidence(evidenceId, linkId) {
    if (!evidenceId || !linkId) throw new Error('evidenceId and linkId are required');
    const res = await this.fetch(
      this.#url('/evidence/' + encodeURIComponent(evidenceId) + '/links/' + encodeURIComponent(linkId)),
      { method: 'DELETE', credentials: 'include' }
    );
    return this.#parse(res, true);
  }

  // ── Documents and suggestions ──────────────────────────────────────

  async listDocuments({ scopeId } = {}) {
    if (!isUserLoggedIn()) return { documents: [] };
    const res = await this.fetch(this.#url('/documents', { scopeId }), { credentials: 'include' });
    return this.#parse(res);
  }

  /** Multipart: the browser sets the boundary, so no Content-Type header here. */
  async uploadDocument({ file, scopeId, title }) {
    if (!file || !scopeId) throw new Error('file and scopeId are required');
    const form = new FormData();
    form.append('file', file);
    form.append('scopeId', scopeId);
    if (title) form.append('title', title);
    const res = await this.fetch(this.#url('/documents'), {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    return this.#parse(res);
  }

  async pasteDocumentText(documentId, text) {
    if (!documentId) throw new Error('documentId is required');
    const res = await this.#json('POST', '/documents/' + encodeURIComponent(documentId) + '/text', { text });
    return this.#parse(res);
  }

  async extractDocument(documentId) {
    if (!documentId) throw new Error('documentId is required');
    const res = await this.#json('POST', '/documents/' + encodeURIComponent(documentId) + '/extract');
    return this.#parse(res);
  }

  async listSuggestions(documentId, { status = 'pending' } = {}) {
    if (!documentId) throw new Error('documentId is required');
    const res = await this.fetch(
      this.#url('/documents/' + encodeURIComponent(documentId) + '/suggestions', { status }),
      { credentials: 'include' }
    );
    return this.#parse(res);
  }

  async acceptSuggestion(suggestionId, edits = {}) {
    if (!suggestionId) throw new Error('suggestionId is required');
    const res = await this.#json('POST', '/suggestions/' + encodeURIComponent(suggestionId) + '/accept', edits);
    return this.#parse(res);
  }

  async rejectSuggestion(suggestionId, reason) {
    if (!suggestionId) throw new Error('suggestionId is required');
    const res = await this.#json('POST', '/suggestions/' + encodeURIComponent(suggestionId) + '/reject', { reason });
    return this.#parse(res);
  }

  /** Download goes through a redirect to a short-lived signed URL. */
  documentDownloadUrl(documentId) {
    return this.#url('/documents/' + encodeURIComponent(documentId) + '/download');
  }

  // ── Ask AgilePro, conversations, Attention, embeddings (spec 004) ─

  async askQuestion({ question, scopeId, conversationId, continueAcrossScope } = {}) {
    const res = await this.#json('POST', '/ask', {
      question,
      scopeId,
      conversationId: conversationId || undefined,
      continueAcrossScope: continueAcrossScope === true ? true : undefined,
    });
    return this.#parse(res);
  }

  async getAttention(scopeId) {
    if (!scopeId) throw new Error('scopeId is required');
    if (!isUserLoggedIn()) return { skipped: true };
    const res = await this.fetch(this.#url('/attention', { scopeId }), { credentials: 'include' });
    return this.#parse(res);
  }

  async getEmbeddingStatus(scopeId) {
    if (!isUserLoggedIn()) return { skipped: true };
    const res = await this.fetch(this.#url('/embeddings/status', { scopeId }), { credentials: 'include' });
    return this.#parse(res);
  }

  // ── Investigations, hypotheses, constraints, patterns (spec 005) ───

  async listInvestigations({ status, scopeId, limit, cursor } = {}) {
    if (!isUserLoggedIn()) return { investigations: [], nextCursor: null, skipped: true };
    const res = await this.fetch(this.#url('/investigations', { status, scopeId, limit, cursor }), { credentials: 'include' });
    return this.#parse(res);
  }

  async getInvestigation(investigationId) {
    if (!investigationId) throw new Error('investigationId is required');
    const res = await this.fetch(this.#url('/investigations/' + encodeURIComponent(investigationId)), { credentials: 'include' });
    return this.#parse(res);
  }

  async openInvestigation({ question, scopeId }) {
    const res = await this.#json('POST', '/investigations', { question, scopeId });
    return this.#parse(res);
  }

  async closeInvestigation(investigationId, { status, conclusion }) {
    if (!investigationId) throw new Error('investigationId is required');
    const res = await this.#json('POST', '/investigations/' + encodeURIComponent(investigationId) + '/close', { status, conclusion });
    return this.#parse(res);
  }

  async reopenInvestigation(investigationId) {
    if (!investigationId) throw new Error('investigationId is required');
    const res = await this.#json('POST', '/investigations/' + encodeURIComponent(investigationId) + '/reopen');
    return this.#parse(res);
  }

  async suggestedInvestigationEvidence(investigationId, { limit } = {}) {
    if (!investigationId) throw new Error('investigationId is required');
    const res = await this.fetch(
      this.#url('/investigations/' + encodeURIComponent(investigationId) + '/suggested-evidence', { limit }),
      { credentials: 'include' }
    );
    return this.#parse(res);
  }

  async attachInvestigationEvidence(investigationId, { evidenceId, note }) {
    if (!investigationId) throw new Error('investigationId is required');
    const res = await this.#json('POST', '/investigations/' + encodeURIComponent(investigationId) + '/evidence', { evidenceId, note });
    return this.#parse(res);
  }

  async detachInvestigationEvidence(investigationId, evidenceId) {
    if (!investigationId || !evidenceId) throw new Error('investigationId and evidenceId are required');
    const res = await this.fetch(
      this.#url('/investigations/' + encodeURIComponent(investigationId) + '/evidence/' + encodeURIComponent(evidenceId)),
      { method: 'DELETE', credentials: 'include' }
    );
    return this.#parse(res, true);
  }

  async generateHypotheses(investigationId) {
    if (!investigationId) throw new Error('investigationId is required');
    const res = await this.#json('POST', '/investigations/' + encodeURIComponent(investigationId) + '/hypotheses', {});
    return this.#parse(res);
  }

  async createHypothesis(investigationId, statement) {
    if (!investigationId) throw new Error('investigationId is required');
    const res = await this.#json('POST', '/investigations/' + encodeURIComponent(investigationId) + '/hypotheses/manual', { statement });
    return this.#parse(res);
  }

  async acceptHypothesisSuggestion(suggestionId, { statement } = {}) {
    if (!suggestionId) throw new Error('suggestionId is required');
    const res = await this.#json('POST', '/hypothesis-suggestions/' + encodeURIComponent(suggestionId) + '/accept', { statement });
    return this.#parse(res);
  }

  async rejectHypothesisSuggestion(suggestionId, reason) {
    if (!suggestionId) throw new Error('suggestionId is required');
    const res = await this.#json('POST', '/hypothesis-suggestions/' + encodeURIComponent(suggestionId) + '/reject', { reason });
    return this.#parse(res);
  }

  async searchContradictions(hypothesisId) {
    if (!hypothesisId) throw new Error('hypothesisId is required');
    const res = await this.#json('POST', '/hypotheses/' + encodeURIComponent(hypothesisId) + '/contradictions', {});
    return this.#parse(res);
  }

  async attachHypothesisEvidence(hypothesisId, { evidenceId, stance, reason }) {
    if (!hypothesisId) throw new Error('hypothesisId is required');
    const res = await this.#json('POST', '/hypotheses/' + encodeURIComponent(hypothesisId) + '/evidence', { evidenceId, stance, reason });
    return this.#parse(res);
  }

  async detachHypothesisEvidence(hypothesisId, evidenceId) {
    if (!hypothesisId || !evidenceId) throw new Error('hypothesisId and evidenceId are required');
    const res = await this.fetch(
      this.#url('/hypotheses/' + encodeURIComponent(hypothesisId) + '/evidence/' + encodeURIComponent(evidenceId)),
      { method: 'DELETE', credentials: 'include' }
    );
    return this.#parse(res);
  }

  async withdrawHypothesis(hypothesisId) {
    if (!hypothesisId) throw new Error('hypothesisId is required');
    const res = await this.#json('POST', '/hypotheses/' + encodeURIComponent(hypothesisId) + '/withdraw');
    return this.#parse(res);
  }

  async listConstraints({ scopeId, status, category, limit, cursor } = {}) {
    if (!isUserLoggedIn()) return { constraints: [], nextCursor: null, skipped: true };
    const res = await this.fetch(this.#url('/constraints', { scopeId, status, category, limit, cursor }), { credentials: 'include' });
    return this.#parse(res);
  }

  async getConstraint(constraintId) {
    if (!constraintId) throw new Error('constraintId is required');
    const res = await this.fetch(this.#url('/constraints/' + encodeURIComponent(constraintId)), { credentials: 'include' });
    return this.#parse(res);
  }

  async recordConstraint(constraint) {
    const res = await this.#json('POST', '/constraints', constraint);
    return this.#parse(res);
  }

  async updateConstraint(constraintId, changes) {
    if (!constraintId) throw new Error('constraintId is required');
    const res = await this.#json('PATCH', '/constraints/' + encodeURIComponent(constraintId), changes);
    return this.#parse(res);
  }

  async linkConstraint(constraintId, { targetKind, targetId, role }) {
    if (!constraintId) throw new Error('constraintId is required');
    const res = await this.#json('POST', '/constraints/' + encodeURIComponent(constraintId) + '/links', { targetKind, targetId, role });
    return this.#parse(res);
  }

  async unlinkConstraint(constraintId, linkId) {
    if (!constraintId || !linkId) throw new Error('constraintId and linkId are required');
    const res = await this.fetch(
      this.#url('/constraints/' + encodeURIComponent(constraintId) + '/links/' + encodeURIComponent(linkId)),
      { method: 'DELETE', credentials: 'include' }
    );
    return this.#parse(res);
  }

  async listPatterns(scopeId, { status } = {}) {
    if (!scopeId) throw new Error('scopeId is required');
    if (!isUserLoggedIn()) return { skipped: true };
    const res = await this.fetch(this.#url('/patterns', { scopeId, status }), { credentials: 'include' });
    return this.#parse(res);
  }

  async detectPatterns(scopeId) {
    if (!scopeId) throw new Error('scopeId is required');
    const res = await this.#json('POST', '/patterns/detect', { scopeId });
    return this.#parse(res);
  }

  async dismissDetection(detectionId, reason) {
    if (!detectionId) throw new Error('detectionId is required');
    const res = await this.#json('POST', '/patterns/' + encodeURIComponent(detectionId) + '/dismiss', { reason });
    return this.#parse(res);
  }

  async investigateDetection(detectionId) {
    if (!detectionId) throw new Error('detectionId is required');
    const res = await this.#json('POST', '/patterns/' + encodeURIComponent(detectionId) + '/investigate', {});
    return this.#parse(res);
  }

  // ── Interventions and their signals (spec 006) ─────────────────────

  async listInterventions({ scopeId, status, limit, cursor } = {}) {
    // Not an answer, and marked as such: the mode can be revealed before the
    // session is restored, and a list fetched then must not be cached as
    // "this organization has no interventions".
    if (!isUserLoggedIn()) return { interventions: [], nextCursor: null, skipped: true };
    const res = await this.fetch(
      this.#url('/interventions', { scopeId, status, limit, cursor }),
      { credentials: 'include' }
    );
    return this.#parse(res);
  }

  async getIntervention(interventionId) {
    if (!interventionId) throw new Error('interventionId is required');
    const res = await this.fetch(
      this.#url('/interventions/' + encodeURIComponent(interventionId)),
      { credentials: 'include' }
    );
    return this.#parse(res);
  }

  async recordIntervention(intervention) {
    const res = await this.#json('POST', '/interventions', intervention);
    return this.#parse(res);
  }

  async updateIntervention(interventionId, changes) {
    if (!interventionId) throw new Error('interventionId is required');
    const res = await this.#json(
      'PATCH', '/interventions/' + encodeURIComponent(interventionId), changes
    );
    return this.#parse(res);
  }

  async attachInterventionEvidence(interventionId, { evidenceId, note } = {}) {
    if (!interventionId) throw new Error('interventionId is required');
    const res = await this.#json(
      'POST', '/interventions/' + encodeURIComponent(interventionId) + '/evidence',
      { evidenceId, note }
    );
    return this.#parse(res);
  }

  async detachInterventionEvidence(interventionId, evidenceId) {
    if (!interventionId || !evidenceId) throw new Error('interventionId and evidenceId are required');
    const res = await this.fetch(
      this.#url('/interventions/' + encodeURIComponent(interventionId)
        + '/evidence/' + encodeURIComponent(evidenceId)),
      { method: 'DELETE', credentials: 'include' }
    );
    return this.#parse(res);
  }

  async addInterventionSignal(interventionId, signal) {
    if (!interventionId) throw new Error('interventionId is required');
    const res = await this.#json(
      'POST', '/interventions/' + encodeURIComponent(interventionId) + '/signals', signal
    );
    return this.#parse(res);
  }

  async updateInterventionSignal(interventionId, signalId, changes) {
    if (!interventionId || !signalId) throw new Error('interventionId and signalId are required');
    const res = await this.#json(
      'PATCH', '/interventions/' + encodeURIComponent(interventionId)
        + '/signals/' + encodeURIComponent(signalId),
      changes
    );
    return this.#parse(res);
  }

  async removeInterventionSignal(interventionId, signalId) {
    if (!interventionId || !signalId) throw new Error('interventionId and signalId are required');
    const res = await this.fetch(
      this.#url('/interventions/' + encodeURIComponent(interventionId)
        + '/signals/' + encodeURIComponent(signalId)),
      { method: 'DELETE', credentials: 'include' }
    );
    return this.#parse(res);
  }

  async recordSignalReading(interventionId, signalId, reading) {
    if (!interventionId || !signalId) throw new Error('interventionId and signalId are required');
    const res = await this.#json(
      'POST', '/interventions/' + encodeURIComponent(interventionId)
        + '/signals/' + encodeURIComponent(signalId) + '/readings',
      reading
    );
    return this.#parse(res);
  }

  async listSignalReadings(interventionId, signalId) {
    if (!interventionId || !signalId) throw new Error('interventionId and signalId are required');
    const res = await this.fetch(
      this.#url('/interventions/' + encodeURIComponent(interventionId)
        + '/signals/' + encodeURIComponent(signalId) + '/readings'),
      { credentials: 'include' }
    );
    return this.#parse(res);
  }

  async runInterventionReview(interventionId) {
    if (!interventionId) throw new Error('interventionId is required');
    const res = await this.#json(
      'POST', '/interventions/' + encodeURIComponent(interventionId) + '/review', {}
    );
    return this.#parse(res);
  }

  async completeInterventionReview(reviewId, completion) {
    if (!reviewId) throw new Error('reviewId is required');
    const res = await this.#json(
      'POST', '/reviews/' + encodeURIComponent(reviewId) + '/complete', completion
    );
    return this.#parse(res);
  }

  // ── Lessons (spec 006) ─────────────────────────────────────────────

  async listLessons({ category, interventionId, limit, cursor } = {}) {
    if (!isUserLoggedIn()) return { lessons: [], nextCursor: null, skipped: true };
    const res = await this.fetch(
      this.#url('/lessons', { category, interventionId, limit, cursor }),
      { credentials: 'include' }
    );
    return this.#parse(res);
  }

  async getLesson(lessonId) {
    if (!lessonId) throw new Error('lessonId is required');
    const res = await this.fetch(
      this.#url('/lessons/' + encodeURIComponent(lessonId)), { credentials: 'include' }
    );
    return this.#parse(res);
  }

  async recordLesson(lesson) {
    const res = await this.#json('POST', '/lessons', lesson);
    return this.#parse(res);
  }

  async recallMemory({ question, scopeId } = {}) {
    const res = await this.#json('POST', '/memory', { question, scopeId });
    return this.#parse(res);
  }

  // ── Briefs (spec 006) ──────────────────────────────────────────────

  async listBriefs({ scopeId, limit, cursor } = {}) {
    if (!isUserLoggedIn()) return { briefs: [], nextCursor: null, skipped: true };
    const res = await this.fetch(
      this.#url('/briefs', { scopeId, limit, cursor }), { credentials: 'include' }
    );
    return this.#parse(res);
  }

  async getBrief(briefId) {
    if (!briefId) throw new Error('briefId is required');
    const res = await this.fetch(
      this.#url('/briefs/' + encodeURIComponent(briefId)), { credentials: 'include' }
    );
    return this.#parse(res);
  }

  async generateBrief({ scopeId, periodStart, periodEnd } = {}) {
    const res = await this.#json('POST', '/briefs', { scopeId, periodStart, periodEnd });
    return this.#parse(res);
  }

  // -- Connectors (spec 007) ------------------------------------------
  //
  // A credential travels out through create and update and never comes back:
  // no response from any of these carries one, so nothing here reads it.

  async listConnectors({ sourceSystem, status } = {}) {
    // Signed out, this is "we do not know yet", not "no connectors". Answering
    // with an empty list would paint the empty state over a real one.
    if (!isUserLoggedIn()) return { connectors: [], skipped: true };
    const res = await this.fetch(
      this.#url('/connectors', { sourceSystem, status }), { credentials: 'include' }
    );
    return this.#parse(res);
  }

  async getConnector(connectorId) {
    if (!connectorId) throw new Error('connectorId is required');
    const res = await this.fetch(
      this.#url('/connectors/' + encodeURIComponent(connectorId)), { credentials: 'include' }
    );
    return this.#parse(res);
  }

  async createConnector(connector) {
    const res = await this.#json('POST', '/connectors', connector);
    return this.#parse(res);
  }

  async updateConnector(connectorId, changes) {
    if (!connectorId) throw new Error('connectorId is required');
    const res = await this.#json(
      'PATCH', '/connectors/' + encodeURIComponent(connectorId), changes
    );
    return this.#parse(res);
  }

  async testConnector(connectorId) {
    if (!connectorId) throw new Error('connectorId is required');
    const res = await this.#json(
      'POST', '/connectors/' + encodeURIComponent(connectorId) + '/test'
    );
    return this.#parse(res);
  }

  /**
   * Start a sync. Answers 202: the work is under way, and the run history is
   * where a member sees what it did.
   */
  async runSync(connectorId, { fullRead = false } = {}) {
    if (!connectorId) throw new Error('connectorId is required');
    const res = await this.#json(
      'POST', '/connectors/' + encodeURIComponent(connectorId) + '/sync', { fullRead }
    );
    return this.#parse(res);
  }

  async listSyncRuns(connectorId, { limit, cursor } = {}) {
    if (!connectorId) throw new Error('connectorId is required');
    const res = await this.fetch(
      this.#url('/connectors/' + encodeURIComponent(connectorId) + '/runs', { limit, cursor }),
      { credentials: 'include' }
    );
    return this.#parse(res);
  }

  async deleteConnector(connectorId) {
    if (!connectorId) throw new Error('connectorId is required');
    const res = await this.fetch(
      this.#url('/connectors/' + encodeURIComponent(connectorId)),
      { method: 'DELETE', credentials: 'include' }
    );
    return this.#parse(res, true);
  }

  // -- Entity resolution (spec 007) -----------------------------------

  async listEntityResolutions({ status, connectorId, limit, cursor } = {}) {
    if (!isUserLoggedIn()) return { resolutions: [], skipped: true };
    const res = await this.fetch(
      this.#url('/resolutions', { status, connectorId, limit, cursor }),
      { credentials: 'include' }
    );
    return this.#parse(res);
  }

  async decideEntityResolution(resolutionId, decision) {
    if (!resolutionId) throw new Error('resolutionId is required');
    const res = await this.#json(
      'POST', '/resolutions/' + encodeURIComponent(resolutionId) + '/decide', { decision }
    );
    return this.#parse(res);
  }

  async createEntityFromMention(resolutionId, entity) {
    if (!resolutionId) throw new Error('resolutionId is required');
    const res = await this.#json(
      'POST', '/resolutions/' + encodeURIComponent(resolutionId) + '/entity', entity
    );
    return this.#parse(res);
  }

  async createScopeFromMention(resolutionId, scope) {
    if (!resolutionId) throw new Error('resolutionId is required');
    const res = await this.#json(
      'POST', '/resolutions/' + encodeURIComponent(resolutionId) + '/scope', scope
    );
    return this.#parse(res);
  }

  async #parse(response, allowEmpty = false) {
    if (!response) {
      throw new Error('Network error');
    }
    if (!response.ok) {
      // 404 across the whole area means the organization does not have
      // Intelligence enabled; callers treat it as "not available".
      let message = 'HTTP ' + response.status;
      let body = null;
      try {
        body = await response.json();
        if (body && body.error) message = body.error;
      } catch (_) {
        // Not JSON; keep the status message.
      }
      const error = new Error(message);
      error.status = response.status;
      // The rest of the body travels with the error: a 409 scope_changed
      // names the conversation's scope, and the service needs it.
      if (body && typeof body === 'object') {
        const { error: _omitted, ...rest } = body;
        error.body = rest;
      }
      throw error;
    }
    const contentType = response.headers && typeof response.headers.get === 'function'
      ? response.headers.get('content-type')
      : null;
    if (allowEmpty && (!contentType || contentType.indexOf('json') === -1)) {
      return null;
    }
    if (contentType && contentType.indexOf('json') !== -1) {
      return response.json();
    }
    return null;
  }
}

if (typeof window !== 'undefined') {
  window.AgileProGateways = window.AgileProGateways || {};
  window.AgileProGateways.IntelligenceGateway = IntelligenceGateway;
}
